import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { useReactTable, getCoreRowModel, getSortedRowModel, createColumnHelper, flexRender } from '@tanstack/react-table';
import axiosInstance from '../axiosInstance.tsx';
import { DataTable } from '../components/DataTable.tsx';
import { SearchInput } from '../components/Input.tsx';
import { Badge } from '../components/Badge.tsx';
import { ArrowIcon } from '../components/UsersTable.tsx';
import { CountryMenu } from '../components/UserMenu.tsx';
import twemoji from 'twemoji';
import { NewCountryModal } from '../components/UserModal.tsx';
import { FaPlus, FaPlusCircle } from 'react-icons/fa';

const PageWrapper = styled.div`
  display: flex;
  flex-direction: column;
  padding: 20px;
  gap: 36px;
`;

const TableContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

type Country = {
  id: number;
  name: string;
  code: string;
  price: number;
  blocked: boolean;
  enabled: boolean;
  numbers: string[];
  for_sell: boolean;
};

const columnHelper = createColumnHelper<Country>();


const BadgesWrapper = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  max-width: 240px;
  align-items: center;
`;


// JSX флаги
const getFlags = (country: Country) => {
  return (
    <BadgesWrapper>
      <Badge
        color={country.blocked ? "#FF474A" : "var(--text-active-color)"}
        label={country.blocked ? "Заблокировано" : "Разрешено"}
        paintLabel={true}
      />

      <Badge
        paintLabel={true}
        color={country.enabled ? "var(--text-active-color)" : "var(--text-color)"}
        label={country.enabled ? "Включено" : "Выключено"}
      />

      <Badge
        color={country.for_sell ? "#212CFF" : "var(--text-color)"}
        paintLabel={true}
        label={country.for_sell ? "На продажу" : "Не на продажу"}
      />
    </BadgesWrapper>
  );
};


const allCountries = {"RU": "Россия", "IT": "Италия", "US": "США", "GB": "Великобритания", "FR": "Франция", "DE": "Германия", "ES": "Испания", "CN": "Китай", "JP": "Япония", "KR": "Корея", "UA": "Украина", "BY": "Беларусь", "KZ": "Казахстан", "PL": "Польша", "NL": "Нидерланды", "BE": "Бельгия", "CH": "Швейцария", "AT": "Австрия", "SE": "Швеция", "NO": "Норвегия", "DK": "Дания", "FI": "Финляндия", "PT": "Португалия", "GR": "Греция", "TR": "Турция", "IL": "Израиль", "SA": "Аравия", "AE": "ОАЭ", "IN": "Индия", "PK": "Пакистан", "BD": "Бангладеш", "BR": "Бразилия", "AR": "Аргентина", "MX": "Мексика", "CA": "Канада", "AU": "Австралия", "NZ": "Зеландия", "ZA": "Республика", "NG": "Нигерия", "EG": "Египет", "MA": "Марокко", "KE": "Кения", "VN": "Вьетнам", "TH": "Таиланд", "ID": "Индонезия", "PH": "Филиппины", "SG": "Сингапур", "HK": "Гонконг", "TW": "Тайвань", "CZ": "Чехия", "SK": "Словакия", "HU": "Венгрия", "RO": "Румыния", "BG": "Болгария", "HR": "Хорватия", "SI": "Словения", "LT": "Литва", "LV": "Латвия", "EE": "Эстония", "IS": "Исландия", "IE": "Ирландия", "LI": "Лихтенштейн", "LU": "Люксембург", "AF": "Афганистан", "AX": "острова", "AL": "Албания", "DZ": "Алжир", "AS": "Самоа", "AD": "Андорра", "AO": "Ангола", "AI": "Ангилья", "AQ": "Антарктика", "AG": "Барбуда", "AM": "Армения", "AW": "Аруба", "AZ": "Азербайджан", "BS": "Багамы", "BH": "Бахрейн", "BB": "Барбадос", "BZ": "Белиз", "BJ": "Бенин", "BM": "Бермуды", "BT": "Бутан", "BO": "Боливия", "BQ": "Саба", "BA": "Герцеговина", "BW": "Ботсвана", "BV": "Буве", "IO": "океане", "BN": "Бруней-Даруссалам", "BF": "Буркина-Фасо", "BI": "Бурунди", "CV": "Кабо-Верде", "KH": "Камбоджа", "CM": "Камерун", "KY": "острова", "CF": "Республика", "TD": "Чад", "CL": "Чили", "CX": "Рождества", "CC": "острова", "CO": "Колумбия", "KM": "Коморы", "CG": "Конго", "CD": "Республика", "CK": "Кука", "CR": "Коста-Рика", "CI": "Кот-д’Ивуар", "CU": "Куба", "CW": "Кюрасао", "CY": "Кипр", "DJ": "Джибути", "DM": "Доминика", "DO": "Республика", "EC": "Эквадор", "SV": "Сальвадор", "GQ": "Гвинея", "ER": "Эритрея", "ET": "Эфиопия", "FK": "острова", "FO": "острова", "FJ": "Фиджи", "GF": "Гвиана", "PF": "Полинезия", "TF": "территории", "GA": "Габон", "GM": "Гамбия", "GE": "Грузия", "GH": "Гана", "GI": "Гибралтар", "GL": "Гренландия", "GD": "Гренада", "GP": "Гваделупа", "GU": "Гуам", "GT": "Гватемала", "GG": "Гернси", "GN": "Гвинея", "GW": "Гвинея-Бисау", "GY": "Гайана", "HT": "Гаити", "HM": "Макдональд", "VA": "Ватикан", "HN": "Гондурас", "IR": "Иран", "IQ": "Ирак", "IM": "Мэн", "JM": "Ямайка", "JE": "Джерси", "JO": "Иордания", "KI": "Кирибати", "KP": "КНДР", "KW": "Кувейт", "KG": "Кыргызстан", "LA": "Лаос", "LB": "Ливан", "LS": "Лесото", "LR": "Либерия", "LY": "Ливия", "MO": "Макао", "MK": "Македония", "MG": "Мадагаскар", "MW": "Малави", "MY": "Малайзия", "MV": "Мальдивы", "ML": "Мали", "MT": "Мальта", "MH": "острова", "MQ": "Мартиника", "MR": "Мавритания", "MU": "Маврикий", "YT": "Майотта", "FM": "Микронезия", "MD": "Молдова", "MC": "Монако", "MN": "Монголия", "ME": "Черногория", "MS": "Монтсеррат", "MZ": "Мозамбик", "MM": "Мьянма", "NA": "Намибия", "NR": "Науру", "NP": "Непал", "NC": "Каледония", "NI": "Никарагуа", "NE": "Нигер", "NU": "Ниуэ", "NF": "Норфолк", "MP": "острова", "OM": "Оман", "PW": "Палау", "PS": "Палестина", "PA": "Панама", "PG": "Гвинея", "PY": "Парагвай", "PE": "Перу", "PN": "Питкэрн", "PR": "Пуэрто-Рико", "QA": "Катар", "RE": "Реюньон", "RW": "Руанда", "BL": "Сен-Бартелеми", "SH": "Елены", "KN": "Невис", "LC": "Сент-Люсия", "MF": "Сен-Мартен", "PM": "Микелон", "VC": "Гренадины", "WS": "Самоа", "SM": "Сан-Марино", "ST": "Принсипи", "SN": "Сенегал", "RS": "Сербия", "SC": "Острова", "SL": "Сьерра-Леоне", "SX": "Синт-Мартен", "SB": "Острова", "SO": "Сомали", "GS": "острова", "SS": "Судан", "LK": "Шри-Ланка", "SD": "Судан", "SR": "Суринам", "SJ": "Ян-Майен", "SY": "Сирия", "TJ": "Таджикистан", "TZ": "Танзания", "TL": "Тимор-Лесте", "TG": "Того", "TK": "Токелау", "TO": "Тонга", "TT": "Тобаго", "TN": "Тунис", "TM": "Туркменистан", "TC": "Кайкос", "TV": "Тувалу", "UG": "Уганда", "UM": "США", "UY": "Уругвай", "UZ": "Узбекистан", "VU": "Вануату", "VE": "Венесуэла", "VG": "острова", "VI": "(США)", "WF": "Футуна", "EH": "Сахара", "YE": "Йемен", "ZM": "Замбия", "ZW": "Зимбабве"}

const countryList = Object.entries(allCountries).map(
  ([code, name]) => `${code} | ${name}`
);
const columns = [
  columnHelper.accessor('name', {
    header: 'Название',
    cell: info => <div dangerouslySetInnerHTML={{__html: twemoji.parse(info.getValue(),
    {  folder: "72x72",
  ext: ".png",
   attributes: () => ({
    style: "width:13px; height:13px;  margin-right: 4px; vertical-align: middle;"
  })})}}></div>,
  }),
  columnHelper.accessor('code', {
    header: 'Код страны',
    cell: info => <div>{info.getValue()}</div>,
  }),
  columnHelper.accessor('numbers', {
    header: 'Префиксы',
    cell: info => {
      const numbers = info.getValue() ?? [];
      return <div>{numbers.join(', ')}</div>;
    },
  }),
  columnHelper.display({
    id: 'flags',
    header: 'Флаги',
    cell: info => getFlags(info.row.original),
  }),
  columnHelper.accessor('price', {
    header: 'Цена',
    cell: info => <div>${info.getValue().toFixed(2)}</div>,
  }),
];

export const CountriesPage: React.FC = ({data, setData, loading}) => {
  const [isModalOpen, setIsModalOpen] = useState(false);

  const [sorting, setSorting] = useState<any[]>([]);
  const [searchId, setSearchId] = useState<string>('');
  const [expandedRows, setExpandedRows] = useState<Set<Number>>(new Set()); 



    const toggleRow = (id: number) => {
    setExpandedRows(prev => {
        const newSet = new Set(prev);
        if (newSet.has(id)) {
        newSet.delete(id);
        } else {
          newSet.clear();
        newSet.add(id);
        }
        return newSet;
    });
    };


    const filteredData = React.useMemo(
    () => (searchId ? data.filter(country => ((country.name).toLowerCase()).includes(searchId.toLowerCase())) : data),
    [searchId, data]
    );


  const table = useReactTable({
    data: filteredData,
    columns,
    state: { sorting },
    onSortingChange: setSorting,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
  });
const AddButton = styled.div`
  width: 52px;
  height: 40px;
  background-color: #0C0C0C;
  border: 1px solid #1F1F1F;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: transform 0.15s, background-color 0.2s;

  &:hover {
    background-color: #1F1F1F;
    transition: 0.2s;
  }

  & svg {
    width: 24px;
    height: 16px;
    color: white;
  }
`;

const getExpandedRow = (row: number) => {
  // Находим страну по id
  const country =  row;
  if (!country) return null; // если не нашли, ничего не показываем

  return (
    <CountryMenu
    country={country}
    onCountryDelete={() => {
        axiosInstance.delete(`/country?name=${encodeURIComponent(country.id)}`).then(
          (resp) => {
            setData(prevData =>
              prevData.filter(c =>
                c.id !== row.id
              )
            );
          } 
        )

    }}
      flags={{
        enabled: country.enabled, // предполагаем, что есть такое поле
        blocked: country.blocked,
        for_sell: country.for_sell,
      }}
      onFlagChange={(key, value) => {
        axiosInstance.put("/country", {name: country.id, key: key, value: value}).then((resp) => {
            setData(prevData =>
          prevData.map(c =>
            c.id === row.id ? { ...c, [key]: value } : c
          )
        );
        })
        
      }}
      onAllParametersClick={() => {
        console.log('Все параметры для', country.name);
      }}
    />
  );
};


  return (
    <PageWrapper>
              <NewCountryModal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} availableCountries={countryList} onCountryCreated={(country) => {
  setData((prevData) => [...prevData, country]);
}}
></NewCountryModal>
      <TableContainer>
  <div style={{ display: "flex", alignItems: "center", gap: 12}}>
  <SearchInput
    value={searchId}
    onChange={setSearchId}
    placeholder="Поиск"
    style={{ }} // <-- добавили flex и сняли max-width
  />
    <AddButton onClick={() => setIsModalOpen(true)}>
      <FaPlus />
    </AddButton>
</div>

        

        <DataTable
          selectable={false}
          table={table}
           after={(row) => <ArrowIcon onClick={() => toggleRow(row.id)} active={expandedRows.has(row.id)}/>}
          loading={loading}
          expanded={false}
          expandedRows={expandedRows}
          setExpandedRows={() => {}}
          toggleRow={toggleRow}
          renderExpandedRow={getExpandedRow}
        />
      </TableContainer>
    </PageWrapper>
  );
};
